import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { toast } from "sonner";
import { Loader2, Trophy, Coins } from "lucide-react";

const medal = (rank) => {
  if (rank === 1) return "text-yellow-400 border-yellow-400/40";
  if (rank === 2) return "text-slate-300 border-slate-300/40";
  if (rank === 3) return "text-amber-600 border-amber-600/40";
  return "text-muted-foreground border-white/10";
};

export default function HallOfFame() {
  const { t } = useLanguage();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/hall-of-fame")
      .then(({ data }) => setRows(data))
      .catch(() => { toast.error(t("hof.loadError")); setRows([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="relative z-10 mx-auto max-w-4xl px-6 py-14 md:px-10" data-testid="hall-of-fame-page">
      <div className="flex items-center gap-3">
        <Trophy className="h-6 w-6 text-credit" />
        <span className="font-mono text-xs uppercase tracking-widest text-primary">{t("hof.eyebrow")}</span>
      </div>
      <h1 className="mt-3 font-heading text-4xl font-black tracking-tighter sm:text-6xl">{t("hof.title")}</h1>

      <div className="mt-6 space-y-4 border-l-2 border-credit/50 bg-credit/5 p-6">
        <p className="text-base leading-relaxed text-foreground/90">{t("hof.intro")}</p>
        <p className="text-sm leading-relaxed text-muted-foreground">{t("hof.note")}</p>
      </div>

      <p className="mt-8 font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("hof.subtitle")}</p>

      {!rows ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-8 border border-dashed border-white/15 p-10 text-center text-muted-foreground">{t("hof.empty")}</p>
      ) : (
        <ol className="mt-4 space-y-2">
          {rows.map((r) => (
            <li
              key={r.rank}
              data-testid={`hof-row-${r.rank}`}
              className="flex items-center gap-4 border border-white/10 bg-card p-4 transition-colors hover:bg-white/5"
            >
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center border font-heading text-lg font-black ${medal(r.rank)}`}>
                {r.rank}
              </span>
              <span className="flex-1 truncate font-heading text-lg font-bold">{r.name}</span>
              <span className="flex items-center gap-1.5 font-mono text-credit">
                <Coins className="h-4 w-4" />
                <span className="text-xl font-black">{r.total_staked}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </main>
  );
}
